/**
 * Breadcrumb Navigation History Tracker
 * Tracks page-to-page navigation and maintains breadcrumb trail in sessionStorage
 * Only works for Shopify pages (template.name == 'page')
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'pageBreadcrumbTrail';
  const PREVIOUS_PAGE_KEY = 'previousPageInfo'; // Store previous page before navigation
  const MAX_TRAIL_LENGTH = 5; // Maximum number of pages in trail

  /**
   * Check if URL is a Shopify page
   */
  function isPageUrl(url) {
    if (!url) return false;
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.pathname.startsWith('/pages/');
    } catch (e) {
      // If URL parsing fails, check if pathname starts with /pages/
      return url.includes('/pages/');
    }
  }

  /**
   * Get current page info
   */
  function getCurrentPageInfo() {
    const pathname = window.location.pathname;
    
    // Try to get title from breadcrumb link first (most accurate)
    let pageTitle = '';
    const currentPageLink = document.querySelector('a.page_last_element[aria-current="page"]');
    if (currentPageLink) {
      pageTitle = currentPageLink.textContent.trim();
    }
    
    // Fallback to document title if breadcrumb not found
    if (!pageTitle) {
      const docTitle = document.title || '';
      // Extract page title (remove site name if present)
      pageTitle = docTitle.split('|')[0].trim() || docTitle.split('-')[0].trim() || docTitle.trim();
    }
    
    return {
      url: pathname,
      title: pageTitle
    };
  }

  /**
   * Get referrer info
   */
  function getReferrerInfo() {
    const referrer = document.referrer;
    if (!referrer) return null;

    try {
      const referrerUrl = new URL(referrer);
      const currentUrl = new URL(window.location.href);
      
      // Only process if same domain
      if (referrerUrl.origin !== currentUrl.origin) {
        return null;
      }

      return {
        url: referrerUrl.pathname,
        isPage: isPageUrl(referrerUrl.pathname)
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Get trail from sessionStorage
   */
  function getTrailFromStorage() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Could not read breadcrumb trail from sessionStorage:', e);
    }
    return [];
  }

  /**
   * Save trail to sessionStorage
   */
  function saveTrailToStorage(trail) {
    try {
      // Limit trail length
      const limitedTrail = trail.slice(-MAX_TRAIL_LENGTH);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(limitedTrail));
      return limitedTrail;
    } catch (e) {
      console.warn('Could not save breadcrumb trail to sessionStorage:', e);
      return trail;
    }
  }

  /**
   * Get previous page info from sessionStorage
   */
  function getPreviousPageInfo() {
    try {
      const stored = sessionStorage.getItem(PREVIOUS_PAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Could not read previous page info from sessionStorage:', e);
    }
    return null;
  }

  /**
   * Save current page info for next navigation
   */
  function saveCurrentPageForNext() {
    const currentPage = getCurrentPageInfo();
    if (isPageUrl(currentPage.url)) {
      try {
        sessionStorage.setItem(PREVIOUS_PAGE_KEY, JSON.stringify(currentPage));
      } catch (e) {
        console.warn('Could not save current page info to sessionStorage:', e);
      }
    }
  }

  /**
   * Update breadcrumb trail based on navigation
   */
  function updateBreadcrumbTrail() {
    // Only track if current page is a Shopify page
    const currentPage = getCurrentPageInfo();
    if (!isPageUrl(currentPage.url)) {
      // If not a page, clear trail and previous page info
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(PREVIOUS_PAGE_KEY);
      } catch (e) {
        // Ignore errors
      }
      return;
    }

    const referrerInfo = getReferrerInfo();
    let trail = getTrailFromStorage();

    if (referrerInfo && referrerInfo.isPage) {
      // User came from another page - continue trail
      
      // Get previous page info (saved before navigation)
      const previousPageInfo = getPreviousPageInfo();
      
      // Check if previous page matches referrer URL
      if (previousPageInfo && previousPageInfo.url === referrerInfo.url) {
        // Previous page was saved correctly - add it to trail if not already there
        const previousPageInTrail = trail.find(item => item.url === previousPageInfo.url);
        if (!previousPageInTrail) {
          trail.push({
            url: previousPageInfo.url,
            title: previousPageInfo.title
          });
        }
      } else {
        // Previous page info not found or doesn't match - try to get from trail
        // If referrer is already in trail, we're good
        const referrerInTrail = trail.find(item => item.url === referrerInfo.url);
        if (!referrerInTrail) {
          // Referrer not in trail - add it with URL only (title unknown)
          trail.push({
            url: referrerInfo.url,
            title: referrerInfo.url.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Page'
          });
        }
      }
      
      // Remove current page if it's already in trail (handles back navigation)
      trail = trail.filter(item => item.url !== currentPage.url);
      
      // Add current page to trail
      trail.push({
        url: currentPage.url,
        title: currentPage.title
      });
    } else {
      // User came from non-page (collection, product, external, direct access)
      // Start fresh trail with current page
      trail = [{
        url: currentPage.url,
        title: currentPage.title
      }];
    }

    // Save updated trail
    saveTrailToStorage(trail);
    
    // Save current page info for next navigation
    saveCurrentPageForNext();
  }

  /**
   * Inject breadcrumb history into DOM
   */
  function injectBreadcrumbHistory() {
    // Only run on pages
    const currentPage = getCurrentPageInfo();
    if (!isPageUrl(currentPage.url)) {
      return;
    }

    const trail = getTrailFromStorage();
    if (trail.length === 0) {
      return; // No history to show
    }

    // Find breadcrumbs container
    const breadcrumbsList = document.querySelector('ol.breadcrumbs-list');
    if (!breadcrumbsList) {
      return; // Breadcrumbs not found
    }

    // Find the last item (current page) - we'll insert before it
    const lastItem = breadcrumbsList.querySelector('li.breadcrumbs-list__item:last-child');
    if (!lastItem) {
      return;
    }

    // Filter trail to exclude current page and get only history items
    const historyItems = trail.filter(item => item.url !== currentPage.url);
    
    if (historyItems.length === 0) {
      return; // No history items to show
    }

    // Insert history items before the last item
    historyItems.forEach((item, index) => {
      // Create breadcrumb item
      const listItem = document.createElement('li');
      listItem.className = 'breadcrumbs-list__item';
      
      const link = document.createElement('a');
      link.className = 'breadcrumbs-list__link';
      link.href = item.url;
      link.textContent = item.title || 'Page';
      
      listItem.appendChild(link);
      
      // Add divider after link for all history items
      const dividerSpan = document.createElement('span');
      dividerSpan.className = 'icon';
      dividerSpan.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="7" viewBox="0 0 8 7" fill="none">
<path d="M0.629949 6.74799C0.517949 6.79466 0.415283 6.80866 0.321949 6.78999C0.228616 6.76199 0.153949 6.71532 0.0979492 6.64999C0.0512826 6.57532 0.0232826 6.49132 0.0139493 6.39799C0.00461592 6.30466 0.0232826 6.22066 0.0699492 6.14599C0.116616 6.06199 0.191283 5.99199 0.293949 5.93599L6.56595 3.13599V3.69599L0.293949 0.89599C0.191283 0.83999 0.116616 0.774657 0.0699492 0.69999C0.0232826 0.61599 0.00461592 0.527324 0.0139493 0.43399C0.0232826 0.340657 0.0512826 0.261323 0.0979492 0.19599C0.153949 0.121323 0.228616 0.0699902 0.321949 0.0419905C0.415283 0.0139903 0.517949 0.0279901 0.629949 0.08399L6.59395 2.78599C6.89262 2.91666 7.04195 3.12666 7.04195 3.41599C7.04195 3.55599 7.00462 3.68199 6.92995 3.79399C6.85528 3.89666 6.74328 3.98066 6.59395 4.04599L0.629949 6.74799Z" fill="black" fill-opacity="0.6"/>
</svg>`;
      listItem.appendChild(dividerSpan);

      // Insert before last item
      breadcrumbsList.insertBefore(listItem, lastItem);
    });
  }

  /**
   * Initialize breadcrumb history tracking
   */
  function init() {
    // Update trail on page load (this reads previous page and saves current page)
    updateBreadcrumbTrail();

    // Wait for DOM to be ready, then inject history
    // Use multiple attempts to ensure breadcrumbs are rendered
    function tryInject() {
      const breadcrumbsList = document.querySelector('ol.breadcrumbs-list');
      if (breadcrumbsList) {
        injectBreadcrumbHistory();
      } else {
        // Retry after a short delay if breadcrumbs not found
        setTimeout(tryInject, 50);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(tryInject, 100);
      });
    } else {
      // DOM already loaded, but wait a bit for breadcrumbs to render
      setTimeout(tryInject, 100);
    }

    // Also save page info before user navigates away (as backup)
    window.addEventListener('beforeunload', function() {
      saveCurrentPageForNext();
    });
  }

  // Initialize when script loads
  init();
})();

